/**
 * Received Invites Page (DSA View)
 * DSAs view and respond to invites from Lead Owners
 *
 * Features:
 * - Lazy loading: Pending invites loaded on page load, Accepted invites loaded on tab click
 * - My Interests style UI for accepted invites with full negotiation flow
 * - RespondToCounterDialog integration for direct counter responses
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { TabView, TabPanel } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Card } from 'primereact/card';
import { Paginator } from 'primereact/paginator';
import { Divider } from 'primereact/divider';
import { SelectButton } from 'primereact/selectbutton';
import {
    dsaOfferService,
    getDsaLoanTypeConfig,
    formatCommission,
    DSA_LOAN_TYPES
} from '../../services/dsaOfferService';
import { formatLoanAmount, getInterestStatusConfig } from '../../services/marketplaceService';
import { RespondToCounterDialog } from '../../components/marketplace';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// My Payouts section tabs
const MY_PAYOUTS_TABS = [
    { label: 'My Listed Payouts', icon: 'pi pi-list', route: '/app/dsa-offers/my-offers' },
    { label: 'Payout Enquiries', icon: 'pi pi-inbox', route: '/app/dsa-offers/received-invites' },
    { label: 'Post Payout', icon: 'pi pi-plus', route: '/app/dsa-offers/post' }
];

// Loan type options for filter (from DSA_LOAN_TYPES constant)
const LOAN_TYPE_OPTIONS = [
    { label: 'All Types', value: null },
    ...DSA_LOAN_TYPES.map(lt => ({ label: lt.label, value: lt.value }))
];

const ROWS_PER_PAGE = 10;

// Outline style for Tags - border only, no solid fill
const outlineTagStyle = (severity) => {
    const colors = {
        warning: { background: 'transparent', border: '1.5px solid #EAB308', color: '#EAB308' },
        info: { background: 'transparent', border: '1.5px solid #3B82F6', color: '#3B82F6' },
        success: { background: 'transparent', border: '1.5px solid #22C55E', color: '#22C55E' },
        danger: { background: 'transparent', border: '1.5px solid #EF4444', color: '#EF4444' },
        secondary: { background: 'transparent', border: '1.5px solid #94A3B8', color: '#64748B' },
    };
    return colors[severity] || colors.secondary;
};

const ReceivedInvites = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Track previous filter values and fetch keys to prevent double API calls
    const prevFiltersRef = useRef(null);
    const lastPendingFetchKeyRef = useRef('');
    const lastAcceptedFetchKeyRef = useRef('');

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // View mode
    const [viewMode, setViewMode] = useState('grid');
    const viewOptions = [
        { icon: 'pi pi-th-large', value: 'grid' },
        { icon: 'pi pi-list', value: 'list' }
    ];

    // Separate state for pending invites
    const [pendingInvites, setPendingInvites] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(true);

    // Separate state for accepted invites (lazy loaded)
    const [acceptedInvites, setAcceptedInvites] = useState([]);
    const [acceptedLoading, setAcceptedLoading] = useState(false);
    const [acceptedLoaded, setAcceptedLoaded] = useState(false);

    // Summary counts
    const [summary, setSummary] = useState({ total: 0, pending: 0, interested: 0, declined: 0 });

    // =========================================================================
    // PAGINATION STATE (per tab)
    // =========================================================================
    const [pendingPagination, setPendingPagination] = useState({ first: 0, rows: ROWS_PER_PAGE, total: 0 });
    const [acceptedPagination, setAcceptedPagination] = useState({ first: 0, rows: ROWS_PER_PAGE, total: 0 });

    // =========================================================================
    // FILTER STATE (shared across tabs)
    // =========================================================================
    const [showFilters, setShowFilters] = useState(true); // Show filters by default
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loanTypeFilter, setLoanTypeFilter] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // Response dialog state
    const [showResponseDialog, setShowResponseDialog] = useState(false);
    const [selectedInvite, setSelectedInvite] = useState(null);
    const [responseType, setResponseType] = useState(null);
    const [responseMessage, setResponseMessage] = useState('');
    const [responding, setResponding] = useState(false);

    // RespondToCounter dialog state (for accepted invites with counter offers)
    const [showRespondToCounterDialog, setShowRespondToCounterDialog] = useState(false);
    const [selectedInterest, setSelectedInterest] = useState(null);

    // Debounce search input
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Check if any filter is active
    const hasActiveFilters = debouncedSearch || loanTypeFilter || startDate || endDate;

    // Clear all filters
    const clearFilters = () => {
        setSearchQuery('');
        setDebouncedSearch('');
        setLoanTypeFilter(null);
        setStartDate(null);
        setEndDate(null);
    };

    // Fetch pending invites - called by effect
    const fetchPendingInvites = async (page, filterParams) => {
        setPendingLoading(true);
        try {
            const response = await dsaOfferService.getReceivedInvites({
                status: 'awaiting',
                include_interest: false,
                per_page: ROWS_PER_PAGE,
                page: page,
                ...filterParams
            });

            if (response.success) {
                const data = response.data || {};
                setPendingInvites(data.invites || []);
                setSummary(data.summary || { total: 0, pending: 0, interested: 0, declined: 0 });
                setPendingPagination(prev => ({
                    ...prev,
                    total: data.pagination?.total || data.invites?.length || 0
                }));
            }
        } catch (error) {
            console.error('Failed to fetch pending invites:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load pending invites'
            });
        } finally {
            setPendingLoading(false);
        }
    };

    // Fetch accepted invites - called by effect
    const fetchAcceptedInvites = async (page, filterParams) => {
        setAcceptedLoading(true);
        try {
            const response = await dsaOfferService.getReceivedInvites({
                status: 'interested',
                include_interest: true,
                per_page: ROWS_PER_PAGE,
                page: page,
                ...filterParams
            });

            if (response.success) {
                const data = response.data || {};
                setAcceptedInvites(data.invites || []);
                setAcceptedLoaded(true);
                setAcceptedPagination(prev => ({
                    ...prev,
                    total: data.pagination?.total || data.invites?.length || 0
                }));
            }
        } catch (error) {
            console.error('Failed to fetch accepted invites:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load accepted invites'
            });
        } finally {
            setAcceptedLoading(false);
        }
    };

    // Single effect for pending invites - prevents double API calls
    useEffect(() => {
        const currentFilters = { debouncedSearch, loanTypeFilter, startDate, endDate };
        const prevFilters = prevFiltersRef.current;

        // Check if filters changed
        const filtersChanged = prevFilters !== null && (
            prevFilters.debouncedSearch !== debouncedSearch ||
            prevFilters.loanTypeFilter !== loanTypeFilter ||
            prevFilters.startDate !== startDate ||
            prevFilters.endDate !== endDate
        );

        // If filters changed and not on first page, reset and skip fetch
        if (filtersChanged && pendingPagination.first !== 0) {
            prevFiltersRef.current = currentFilters;
            setPendingPagination(prev => ({ ...prev, first: 0 }));
            setAcceptedPagination(prev => ({ ...prev, first: 0 }));
            setAcceptedLoaded(false);
            return;
        }

        // Build filter params
        const filterParams = {};
        if (debouncedSearch) filterParams.search = debouncedSearch;
        if (loanTypeFilter) filterParams.loan_type = loanTypeFilter;
        if (startDate) filterParams.date_from = startDate.toISOString().split('T')[0];
        if (endDate) filterParams.date_to = endDate.toISOString().split('T')[0];

        // Create fetch key to prevent duplicates
        const fetchKey = JSON.stringify({
            debouncedSearch, loanTypeFilter,
            startDate: startDate?.toISOString(),
            endDate: endDate?.toISOString(),
            first: pendingPagination.first
        });

        if (fetchKey === lastPendingFetchKeyRef.current) {
            return;
        }

        prevFiltersRef.current = currentFilters;
        lastPendingFetchKeyRef.current = fetchKey;

        const page = Math.floor(pendingPagination.first / ROWS_PER_PAGE) + 1;
        fetchPendingInvites(page, filterParams);
    }, [debouncedSearch, loanTypeFilter, startDate, endDate, pendingPagination.first]);

    // Effect for accepted invites - only runs when tab is active (index 1)
    useEffect(() => {
        if (activeTab !== 1) return;
        if (acceptedLoaded) return;

        // Build filter params
        const filterParams = {};
        if (debouncedSearch) filterParams.search = debouncedSearch;
        if (loanTypeFilter) filterParams.loan_type = loanTypeFilter;
        if (startDate) filterParams.date_from = startDate.toISOString().split('T')[0];
        if (endDate) filterParams.date_to = endDate.toISOString().split('T')[0];

        // Create fetch key to prevent duplicates
        const fetchKey = JSON.stringify({
            debouncedSearch, loanTypeFilter,
            startDate: startDate?.toISOString(),
            endDate: endDate?.toISOString(),
            first: acceptedPagination.first
        });

        if (fetchKey === lastAcceptedFetchKeyRef.current) {
            return;
        }

        lastAcceptedFetchKeyRef.current = fetchKey;

        const page = Math.floor(acceptedPagination.first / ROWS_PER_PAGE) + 1;
        fetchAcceptedInvites(page, filterParams);
    }, [activeTab, acceptedLoaded, acceptedPagination.first, debouncedSearch, loanTypeFilter, startDate, endDate]);

    // Handle tab change - refetch data when switching tabs
    const handleTabChange = (e) => {
        setActiveTab(e.index);

        // Build filter params
        const filterParams = {};
        if (debouncedSearch) filterParams.search = debouncedSearch;
        if (loanTypeFilter) filterParams.loan_type = loanTypeFilter;
        if (startDate) filterParams.date_from = startDate.toISOString().split('T')[0];
        if (endDate) filterParams.date_to = endDate.toISOString().split('T')[0];

        // Force refetch when switching to Pending tab (index 0)
        if (e.index === 0) {
            lastPendingFetchKeyRef.current = '';
            const page = Math.floor(pendingPagination.first / ROWS_PER_PAGE) + 1;
            fetchPendingInvites(page, filterParams);
        }

        // Force refetch when switching to Accepted tab (index 1)
        if (e.index === 1) {
            lastAcceptedFetchKeyRef.current = '';
            setAcceptedLoaded(false);
        }
    };

    // Pagination handlers
    const handlePendingPageChange = (e) => {
        setPendingPagination(prev => ({ ...prev, first: e.first, rows: e.rows }));
    };

    const handleAcceptedPageChange = (e) => {
        setAcceptedPagination(prev => ({ ...prev, first: e.first, rows: e.rows }));
        setAcceptedLoaded(false); // Force reload
    };

    // Refresh all data
    const handleRefresh = () => {
        // Clear fetch keys to force refresh
        lastPendingFetchKeyRef.current = '';
        lastAcceptedFetchKeyRef.current = '';

        // Build filter params
        const filterParams = {};
        if (debouncedSearch) filterParams.search = debouncedSearch;
        if (loanTypeFilter) filterParams.loan_type = loanTypeFilter;
        if (startDate) filterParams.date_from = startDate.toISOString().split('T')[0];
        if (endDate) filterParams.date_to = endDate.toISOString().split('T')[0];

        const pendingPage = Math.floor(pendingPagination.first / ROWS_PER_PAGE) + 1;
        fetchPendingInvites(pendingPage, filterParams);

        if (activeTab === 1) {
            setAcceptedLoaded(false);
            const acceptedPage = Math.floor(acceptedPagination.first / ROWS_PER_PAGE) + 1;
            fetchAcceptedInvites(acceptedPage, filterParams);
        }
    };

    // Mark as viewed
    const markAsViewed = async (invite) => {
        if (invite.status === 'pending' && !invite.viewed_at) {
            try {
                await dsaOfferService.markInviteViewed(invite.id);
            } catch (error) {
                console.error('Failed to mark as viewed:', error);
            }
        }
    };

    // Handle View Lead - Navigate to marketplace listing
    const handleViewLead = (invite) => {
        markAsViewed(invite);
        navigate(`/app/marketplace/listing/${invite.marketplace_lead_id}`);
    };

    // Handle accept invite
    const handleAccept = (invite) => {
        markAsViewed(invite);
        setSelectedInvite(invite);
        setResponseType('accept');
        setResponseMessage('');
        setShowResponseDialog(true);
    };

    // Handle decline invite
    const handleDecline = (invite) => {
        markAsViewed(invite);
        confirmDialog({
            message: 'Are you sure you want to decline this enquiry?',
            header: 'Decline Enquiry',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => {
                setSelectedInvite(invite);
                setResponseType('decline');
                setResponseMessage('');
                setShowResponseDialog(true);
            }
        });
    };

    // Submit response (accept/decline)
    const handleSubmitResponse = async () => {
        if (!selectedInvite || !responseType) return;

        setResponding(true);
        try {
            let response;
            if (responseType === 'accept') {
                response = await dsaOfferService.acceptInvite(selectedInvite.id, {
                    message: responseMessage || undefined
                });
            } else {
                response = await dsaOfferService.declineInvite(selectedInvite.id, {
                    reason: responseMessage || undefined
                });
            }

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: responseType === 'accept' ? 'Enquiry Accepted!' : 'Enquiry Declined',
                    detail: responseType === 'accept'
                        ? 'You have expressed interest in this lead.'
                        : 'You have declined this enquiry.'
                });
                setShowResponseDialog(false);
                setSelectedInvite(null);

                // Refresh data
                handleRefresh();

                // Navigate to marketplace listing if accepted
                if (responseType === 'accept' && selectedInvite.marketplace_lead_id) {
                    navigate(`/app/marketplace/listing/${selectedInvite.marketplace_lead_id}`);
                }
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to respond to enquiry'
            });
        } finally {
            setResponding(false);
        }
    };

    // Handle respond to counter offer
    const handleRespondToCounter = (invite) => {
        if (invite.linked_interest) {
            setSelectedInterest(invite.linked_interest);
            setShowRespondToCounterDialog(true);
        }
    };

    // Get quality grade severity
    const getGradeSeverity = (grade) => {
        if (!grade) return 'secondary';
        const g = grade.toUpperCase();
        if (g === 'A' || g === 'A+') return 'success';
        if (g === 'B' || g === 'B+') return 'info';
        if (g === 'C' || g === 'C+') return 'warning';
        return 'danger';
    };

    // =========================================================================
    // PENDING INVITE CARD - Grid card view matching SentInvites structure
    // =========================================================================
    const PendingInviteCard = ({ invite }) => {
        const lead = invite.lead || {};
        const offer = invite.offer || {};
        const invitedBy = invite.invited_by || {};

        const borrowerName = lead.borrower_name || 'Lead';
        const productType = lead.product_type_detailed || lead.product_type || 'personal_loan';
        const loanTypeConfig = getDsaLoanTypeConfig(productType);
        const loanAmount = parseFloat(lead.loan_amount) || 0;
        const locationParts = [lead.city, lead.state].filter(Boolean);
        const qualityGrade = lead.quality_grade;
        const bankName = offer.bank_name || '';
        const commission = offer.commission_percentage || 0;

        const canRespond = invite.can_respond && ['pending', 'viewed'].includes(invite.status);

        return (
            <div className="surface-card border-round shadow-1 overflow-hidden hover:shadow-2 transition-all transition-duration-150 h-full flex flex-column">
                <div className="p-3 flex flex-column gap-2 flex-1">
                    {/* Lead Owner Info */}
                    <div className="flex align-items-center gap-2">
                        <Avatar
                            image={invitedBy.avatar}
                            icon={!invitedBy.avatar ? 'pi pi-user' : null}
                            size="large"
                            shape="circle"
                            className="bg-blue-100 flex-shrink-0"
                            style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.8rem' }}>{invitedBy.name || 'Lead Owner'}</div>
                            <div className="flex align-items-center gap-1 flex-wrap">
                                {bankName && <Tag value={bankName} style={{ ...outlineTagStyle('info'), fontSize: '0.6rem', padding: '1px 5px' }} />}
                                <Tag value={loanTypeConfig.label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Lead Info */}
                    <div>
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>Lead</div>
                        <div className="flex align-items-center gap-2">
                            <div className="font-semibold text-900" style={{ fontSize: '0.8rem' }}>{borrowerName}</div>
                            {qualityGrade && <Tag value={qualityGrade} style={{ ...outlineTagStyle(getGradeSeverity(qualityGrade)), fontSize: '0.55rem', padding: '1px 4px' }} />}
                        </div>
                        <div className="flex align-items-center gap-2 mt-1">
                            <span className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatLoanAmount(loanAmount)}</span>
                            {lead?.product_type && <Tag value={getDsaLoanTypeConfig(lead.product_type).label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />}
                        </div>
                        {locationParts.length > 0 && (
                            <div className="text-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                <i className="pi pi-map-marker mr-1" style={{ fontSize: '0.6rem' }} />{locationParts.join(', ')}
                            </div>
                        )}
                    </div>

                    <Divider className="my-1" />

                    {/* Status row */}
                    <div className="flex flex-wrap gap-3" style={{ fontSize: '0.75rem' }}>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Status</div>
                            <Tag value="Pending" style={{ ...outlineTagStyle('warning'), fontSize: '0.6rem', padding: '2px 5px' }} icon="pi pi-clock" />
                        </div>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>You'll Pay</div>
                            <div className="font-bold text-primary">{formatCommission(commission)}</div>
                        </div>
                        {bankName && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Bank</div>
                                <div className="font-semibold text-700" style={{ fontSize: '0.7rem' }}>{bankName}</div>
                            </div>
                        )}
                        {invite.expiry_text && canRespond && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Expires</div>
                                <Tag value={invite.expiry_text} style={{ ...outlineTagStyle('warning'), fontSize: '0.6rem', padding: '2px 5px' }} icon="pi pi-clock" />
                            </div>
                        )}
                    </div>

                    {/* Message */}
                    {invite.invite_message && (
                        <div className="text-500 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.65rem' }}>
                            <i className="pi pi-comment mr-1 text-blue-500" style={{ fontSize: '0.6rem' }} />
                            "{invite.invite_message}"
                        </div>
                    )}

                    {/* Timestamp */}
                    <div className="text-500" style={{ fontSize: '0.6rem' }}>
                        {invite.time_since_invite}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex align-items-center gap-2 px-3 pb-3 flex-wrap mt-auto">
                    {canRespond && (
                        <>
                            <Button
                                label="Accept"
                                icon="pi pi-check"
                                className="p-button-success"
                                style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                                onClick={() => handleAccept(invite)}
                            />
                            <Button
                                label="Decline"
                                icon="pi pi-times"
                                className="p-button-outlined p-button-danger"
                                style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                                onClick={() => handleDecline(invite)}
                            />
                        </>
                    )}
                    <div className="flex align-items-center gap-1 ml-auto">
                        <Button
                            icon="pi pi-briefcase"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Offer"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/dsa-offers/${offer?.id || invite.dsa_offer_id}`)}
                        />
                        <Button
                            icon="pi pi-external-link"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Lead"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => handleViewLead(invite)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // =========================================================================
    // ACCEPTED INVITE CARD - Grid card view matching SentInvites structure
    // =========================================================================
    const AcceptedInviteCard = ({ invite }) => {
        const lead = invite.lead || {};
        const offer = invite.offer || {};
        const invitedBy = invite.invited_by || {};
        const linkedInterest = invite.linked_interest || null;

        const borrowerName = lead.borrower_name || 'Lead';
        const productType = lead.product_type_detailed || lead.product_type || 'personal_loan';
        const loanTypeConfig = getDsaLoanTypeConfig(productType);
        const loanAmount = parseFloat(lead.loan_amount) || 0;
        const locationParts = [lead.city, lead.state].filter(Boolean);
        const qualityGrade = lead.quality_grade;
        const bankName = offer.bank_name || '';
        const commission = offer.commission_percentage || 0;

        // Interest status
        const statusConfig = linkedInterest ? getInterestStatusConfig(linkedInterest.status) : null;
        const isCountered = linkedInterest?.status === 'countered';
        const isNegotiationAccepted = linkedInterest?.status === 'accepted';
        const hasCollaboration = linkedInterest?.collaboration?.id;
        const isYourTurn = linkedInterest?.is_your_turn;
        const proposedPercentage = linkedInterest?.proposed_percentage || commission;
        const counterPercentage = linkedInterest?.counter_percentage;
        const effectivePercentage = linkedInterest?.effective_percentage || proposedPercentage;
        const negotiationRound = linkedInterest?.negotiation_round || 1;

        return (
            <div className="surface-card border-round shadow-1 overflow-hidden h-full flex flex-column">
                {/* Turn Indicator */}
                {linkedInterest?.status === 'pending' && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-clock text-blue-500" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-blue-600">Under review</span>
                    </div>
                )}
                {isCountered && isYourTurn && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-sync text-orange-500" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-orange-600">Your turn to respond</span>
                    </div>
                )}
                {isCountered && !isYourTurn && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-clock text-yellow-600" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-yellow-700">Awaiting response</span>
                    </div>
                )}
                {isNegotiationAccepted && hasCollaboration && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-check-circle text-green-500" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-green-600">Lead Deal Active</span>
                    </div>
                )}
                {linkedInterest?.status === 'rejected' && (
                    <div className="px-3 pt-3 flex align-items-center gap-2" style={{ fontSize: '0.7rem' }}>
                        <i className="pi pi-times-circle text-red-500" style={{ fontSize: '0.75rem' }} />
                        <span className="font-semibold text-red-600">Interest Rejected</span>
                    </div>
                )}

                <div className="p-3 flex flex-column gap-2 flex-1">
                    {/* Lead Owner Info */}
                    <div className="flex align-items-center gap-2">
                        <Avatar
                            image={invitedBy.avatar}
                            icon={!invitedBy.avatar ? 'pi pi-user' : null}
                            size="large"
                            shape="circle"
                            className="bg-blue-100 flex-shrink-0"
                            style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.8rem' }}>{invitedBy.name || 'Lead Owner'}</div>
                            <div className="flex align-items-center gap-1 flex-wrap">
                                {bankName && <Tag value={bankName} style={{ ...outlineTagStyle('info'), fontSize: '0.6rem', padding: '1px 5px' }} />}
                                <Tag value={loanTypeConfig.label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Lead Info */}
                    <div>
                        <div className="text-500" style={{ fontSize: '0.6rem' }}>Lead</div>
                        <div className="flex align-items-center gap-2">
                            <div className="font-semibold text-900" style={{ fontSize: '0.8rem' }}>{borrowerName}</div>
                            {qualityGrade && <Tag value={qualityGrade} style={{ ...outlineTagStyle(getGradeSeverity(qualityGrade)), fontSize: '0.55rem', padding: '1px 4px' }} />}
                        </div>
                        <div className="flex align-items-center gap-2 mt-1">
                            <span className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatLoanAmount(loanAmount)}</span>
                            {lead?.product_type && <Tag value={getDsaLoanTypeConfig(lead.product_type).label} style={{ ...outlineTagStyle('secondary'), fontSize: '0.55rem', padding: '1px 4px' }} />}
                        </div>
                        {locationParts.length > 0 && (
                            <div className="text-500 mt-1" style={{ fontSize: '0.65rem' }}>
                                <i className="pi pi-map-marker mr-1" style={{ fontSize: '0.6rem' }} />{locationParts.join(', ')}
                            </div>
                        )}
                    </div>

                    <Divider className="my-1" />

                    {/* Negotiation Status */}
                    <div className="flex flex-wrap gap-3" style={{ fontSize: '0.75rem' }}>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Status</div>
                            {statusConfig && (
                                <Tag
                                    value={statusConfig.label}
                                    style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.6rem', padding: '2px 5px' }}
                                    icon={`pi ${statusConfig.icon}`}
                                />
                            )}
                        </div>
                        <div>
                            <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>You Offered</div>
                            <div className="font-bold text-primary">{formatCommission(proposedPercentage)}</div>
                        </div>
                        {counterPercentage && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Counter</div>
                                <div className="font-bold text-orange-500">{formatCommission(counterPercentage)}</div>
                            </div>
                        )}
                        {isNegotiationAccepted && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Final Rate</div>
                                <div className="font-bold text-green-600">{formatCommission(effectivePercentage)}</div>
                            </div>
                        )}
                        {bankName && (
                            <div>
                                <div className="text-500 mb-1" style={{ fontSize: '0.6rem' }}>Bank</div>
                                <div className="font-semibold text-700" style={{ fontSize: '0.7rem' }}>{bankName}</div>
                            </div>
                        )}
                        <div className="flex align-items-center">
                            <span className="text-500" style={{ fontSize: '0.6rem' }}>Round</span>
                            <span className="font-semibold text-700 ml-1" style={{ fontSize: '0.75rem' }}>#{negotiationRound}</span>
                        </div>
                    </div>

                    {/* Messages */}
                    {linkedInterest?.message && (
                        <div className="text-500 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.65rem' }}>
                            <i className="pi pi-comment mr-1 text-blue-500" style={{ fontSize: '0.6rem' }} />
                            You: "{linkedInterest.message}"
                        </div>
                    )}
                    {linkedInterest?.counter_message && (
                        <div className="text-500 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.65rem' }}>
                            <i className="pi pi-reply mr-1 text-orange-500" style={{ fontSize: '0.6rem' }} />
                            Owner: "{linkedInterest.counter_message}"
                        </div>
                    )}

                    {/* Timestamp */}
                    <div className="text-500" style={{ fontSize: '0.6rem' }}>
                        {invite.time_since_invite}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex align-items-center gap-2 px-3 pb-3 flex-wrap mt-auto">
                    {/* Respond to counter */}
                    {isCountered && isYourTurn && (
                        <Button
                            label="Respond"
                            icon="pi pi-reply"
                            className="p-button-warning"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            onClick={() => handleRespondToCounter(invite)}
                        />
                    )}

                    {/* Waiting state */}
                    {isCountered && !isYourTurn && (
                        <Button
                            label="Awaiting"
                            icon="pi pi-clock"
                            className="p-button-secondary p-button-outlined"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            disabled
                        />
                    )}

                    {/* Pending review */}
                    {linkedInterest?.status === 'pending' && (
                        <Button
                            label="Under Review"
                            icon="pi pi-clock"
                            className="p-button-secondary p-button-outlined"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            disabled
                        />
                    )}

                    {/* View Collaboration */}
                    {hasCollaboration && (
                        <Button
                            label="Lead Deal"
                            icon="pi pi-users"
                            className="p-button-success"
                            style={{ fontSize: '0.7rem', padding: '0.35rem 0.6rem' }}
                            onClick={() => navigate(`/app/collaborations/${linkedInterest.collaboration.id}`)}
                        />
                    )}

                    <div className="flex align-items-center gap-1 ml-auto">
                        <Button
                            icon="pi pi-briefcase"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Offer"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => navigate(`/app/dsa-offers/${offer?.id || invite.dsa_offer_id}`)}
                        />
                        <Button
                            icon="pi pi-external-link"
                            className="p-button-text"
                            style={{ padding: '0.35rem' }}
                            tooltip="View Lead"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => handleViewLead(invite)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // =========================================================================
    // LIST VIEW ROW COMPONENTS
    // =========================================================================

    // Row for pending invites (list view)
    const PendingInviteRow = ({ invite }) => {
        const lead = invite.lead || {};
        const offer = invite.offer || {};
        const invitedBy = invite.invited_by || {};
        const borrowerName = lead.borrower_name || 'Lead';
        const loanTypeConfig = getDsaLoanTypeConfig(lead.product_type_detailed || lead.product_type || 'personal_loan');
        const bankName = offer.bank_name || '';
        const commission = offer.commission_percentage || 0;
        const canRespond = invite.can_respond && ['pending', 'viewed'].includes(invite.status);

        return (
            <div className="surface-card border-round shadow-1 mb-2 overflow-hidden border-left-3 border-yellow-400">
                <div className="p-2">
                    <div className="flex flex-column md:flex-row gap-2">
                        {/* Left: Lead Owner + Lead Info */}
                        <div className="flex align-items-start gap-2 flex-1">
                            <Avatar
                                image={invitedBy.avatar}
                                icon={!invitedBy.avatar ? 'pi pi-user' : null}
                                shape="circle"
                                className="bg-blue-100 flex-shrink-0"
                                style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                            />
                            <div className="flex-1 min-w-0">
                                {/* Lead Owner Name + Status + Type */}
                                <div className="flex align-items-center gap-1 mb-1 flex-wrap">
                                    <span className="font-semibold text-900" style={{ fontSize: '0.75rem' }}>{invitedBy.name || 'Lead Owner'}</span>
                                    <Tag value="Pending" style={{ ...outlineTagStyle('warning'), fontSize: '0.55rem', padding: '0.1rem 0.25rem' }} />
                                    <Tag value={loanTypeConfig.label} style={{ fontSize: '0.5rem', padding: '0 0.25rem', backgroundColor: 'var(--primary-100)', color: 'var(--primary-700)', border: 'none' }} />
                                </div>
                                {/* Lead + Amount + Location */}
                                <div className="flex align-items-center gap-2 text-500 flex-wrap" style={{ fontSize: '0.65rem' }}>
                                    <span><i className="pi pi-file mr-1" style={{ fontSize: '0.55rem' }} />{borrowerName}</span>
                                    <span className="text-green-600 font-bold">{formatLoanAmount(lead?.loan_amount)}</span>
                                    {(lead?.city || lead?.state) && (
                                        <span className="flex align-items-center gap-1 hidden md:inline-flex">
                                            <i className="pi pi-map-marker" style={{ fontSize: '0.55rem' }} />
                                            {[lead?.city, lead?.state].filter(Boolean).join(', ')}
                                        </span>
                                    )}
                                </div>
                                {/* Message */}
                                {invite.invite_message && (
                                    <div className="text-500 mt-1 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.6rem' }}>
                                        "{invite.invite_message}"
                                    </div>
                                )}
                                {/* Meta on desktop */}
                                <div className="hidden md:flex align-items-center gap-2 text-400 mt-1" style={{ fontSize: '0.6rem' }}>
                                    <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                    <span>{invite.time_since_invite}</span>
                                    {bankName && (<><span>·</span><span>{bankName}</span></>)}
                                    {invite.expiry_text && canRespond && (
                                        <><span>·</span><span className="text-orange-500">Expires {invite.expiry_text}</span></>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Center: Commission */}
                        <div className="flex md:flex-column align-items-center justify-content-center gap-1 md:px-2 md:border-left-1 md:border-right-1 surface-border">
                            <div className="flex flex-column align-items-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>You'll Pay</div>
                                <div className="bg-blue-50 border-round px-2 py-1">
                                    <span className="text-primary font-bold" style={{ fontSize: '0.75rem' }}>{formatCommission(commission)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex md:flex-column align-items-center md:align-items-end justify-content-between gap-1">
                            <div className="flex md:hidden align-items-center gap-2 text-400" style={{ fontSize: '0.6rem' }}>
                                <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                <span>{invite.time_since_invite}</span>
                            </div>
                            <div className="flex align-items-center gap-1">
                                {canRespond && (
                                    <>
                                        <Button label="Accept" icon="pi pi-check" className="p-button-success" style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} onClick={() => handleAccept(invite)} />
                                        <Button label="Decline" icon="pi pi-times" className="p-button-outlined p-button-danger" style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} onClick={() => handleDecline(invite)} />
                                    </>
                                )}
                                <Button icon="pi pi-briefcase" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Offer" tooltipOptions={{ position: 'top' }} onClick={() => navigate(`/app/dsa-offers/${offer?.id || invite.dsa_offer_id}`)} />
                                <Button icon="pi pi-external-link" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Lead" tooltipOptions={{ position: 'top' }} onClick={() => handleViewLead(invite)} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Row for accepted invites (list view)
    const AcceptedInviteRow = ({ invite }) => {
        const lead = invite.lead || {};
        const offer = invite.offer || {};
        const invitedBy = invite.invited_by || {};
        const linkedInterest = invite.linked_interest || null;
        const loanTypeConfig = getDsaLoanTypeConfig(lead.product_type_detailed || lead.product_type || 'personal_loan');
        const borrowerName = lead.borrower_name || 'Lead';
        const bankName = offer.bank_name || '';
        const statusConfig = linkedInterest ? getInterestStatusConfig(linkedInterest.status) : null;
        const isCountered = linkedInterest?.status === 'countered';
        const isNegotiationAccepted = linkedInterest?.status === 'accepted';
        const hasCollaboration = linkedInterest?.collaboration?.id;
        const isYourTurn = linkedInterest?.is_your_turn;
        const proposedPercentage = linkedInterest?.proposed_percentage;
        const counterPercentage = linkedInterest?.counter_percentage;
        const effectivePercentage = linkedInterest?.effective_percentage || proposedPercentage;
        const negotiationRound = linkedInterest?.negotiation_round || 1;

        const getBorderClass = () => {
            if (isNegotiationAccepted) return 'border-green-400';
            if (isCountered && isYourTurn) return 'border-orange-400';
            if (linkedInterest?.status === 'rejected') return 'border-red-400';
            return 'border-blue-400';
        };

        return (
            <div className={`surface-card border-round shadow-1 mb-2 overflow-hidden border-left-3 ${getBorderClass()}`}>
                <div className="p-2">
                    <div className="flex flex-column md:flex-row gap-2">
                        {/* Left: Lead Owner + Lead Info */}
                        <div className="flex align-items-start gap-2 flex-1">
                            <Avatar
                                image={invitedBy.avatar}
                                icon={!invitedBy.avatar ? 'pi pi-user' : null}
                                shape="circle"
                                className="bg-blue-100 flex-shrink-0"
                                style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.7rem' }}
                            />
                            <div className="flex-1 min-w-0">
                                {/* Lead Owner Name + Status + Type */}
                                <div className="flex align-items-center gap-1 mb-1 flex-wrap">
                                    <span className="font-semibold text-900" style={{ fontSize: '0.75rem' }}>{invitedBy.name || 'Lead Owner'}</span>
                                    {statusConfig && <Tag value={statusConfig.label} style={{ ...outlineTagStyle(statusConfig.severity), fontSize: '0.55rem', padding: '0.1rem 0.25rem' }} />}
                                    <Tag value={loanTypeConfig.label} style={{ fontSize: '0.5rem', padding: '0 0.25rem', backgroundColor: 'var(--primary-100)', color: 'var(--primary-700)', border: 'none' }} />
                                </div>
                                {/* Lead + Amount + Location */}
                                <div className="flex align-items-center gap-2 text-500 flex-wrap" style={{ fontSize: '0.65rem' }}>
                                    <span><i className="pi pi-file mr-1" style={{ fontSize: '0.55rem' }} />{borrowerName}</span>
                                    <span className="text-green-600 font-bold">{formatLoanAmount(lead?.loan_amount)}</span>
                                    {(lead?.city || lead?.state) && (
                                        <span className="flex align-items-center gap-1 hidden md:inline-flex">
                                            <i className="pi pi-map-marker" style={{ fontSize: '0.55rem' }} />
                                            {[lead?.city, lead?.state].filter(Boolean).join(', ')}
                                        </span>
                                    )}
                                </div>
                                {/* Messages */}
                                {linkedInterest?.message && (
                                    <div className="text-500 mt-1 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.6rem' }}>
                                        You: "{linkedInterest.message}"
                                    </div>
                                )}
                                {linkedInterest?.counter_message && (
                                    <div className="text-500 font-italic white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ fontSize: '0.6rem' }}>
                                        Owner: "{linkedInterest.counter_message}"
                                    </div>
                                )}
                                {/* Meta on desktop */}
                                <div className="hidden md:flex align-items-center gap-2 text-400 mt-1" style={{ fontSize: '0.6rem' }}>
                                    <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                    <span>{invite.time_since_invite}</span>
                                    {bankName && (<><span>·</span><span>{bankName}</span></>)}
                                    {negotiationRound > 1 && (<><span>·</span><span>Round #{negotiationRound}</span></>)}
                                </div>
                            </div>
                        </div>

                        {/* Center: Percentages */}
                        <div className="flex md:flex-column align-items-center justify-content-center gap-1 md:px-2 md:border-left-1 md:border-right-1 surface-border">
                            <div className="flex flex-column align-items-center">
                                <div className="text-500" style={{ fontSize: '0.55rem' }}>You Offered</div>
                                <div className="bg-blue-50 border-round px-2 py-1">
                                    <span className="text-primary font-bold" style={{ fontSize: '0.75rem' }}>{formatCommission(proposedPercentage)}</span>
                                </div>
                            </div>
                            {counterPercentage && (
                                <div className="flex flex-column align-items-center">
                                    <div className="text-500" style={{ fontSize: '0.55rem' }}>Counter</div>
                                    <div className="bg-orange-50 border-round px-2 py-1">
                                        <span className="text-orange-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatCommission(counterPercentage)}</span>
                                    </div>
                                </div>
                            )}
                            {isNegotiationAccepted && (
                                <div className="flex flex-column align-items-center">
                                    <div className="text-500" style={{ fontSize: '0.55rem' }}>Final</div>
                                    <div className="bg-green-50 border-round px-2 py-1">
                                        <span className="text-green-600 font-bold" style={{ fontSize: '0.75rem' }}>{formatCommission(effectivePercentage)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right: Actions */}
                        <div className="flex md:flex-column align-items-center md:align-items-end justify-content-between gap-1">
                            <div className="flex md:hidden align-items-center gap-2 text-400" style={{ fontSize: '0.6rem' }}>
                                <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                                <span>{invite.time_since_invite}</span>
                            </div>
                            <div className="flex align-items-center gap-1">
                                {isCountered && isYourTurn && (
                                    <Button label="Respond" icon="pi pi-reply" className="p-button-warning" style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} onClick={() => handleRespondToCounter(invite)} />
                                )}
                                {isCountered && !isYourTurn && (
                                    <Button label="Awaiting" icon="pi pi-clock" className="p-button-outlined p-button-secondary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} disabled />
                                )}
                                {linkedInterest?.status === 'pending' && (
                                    <Button label="Under Review" icon="pi pi-clock" className="p-button-outlined p-button-secondary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} disabled />
                                )}
                                {hasCollaboration && (
                                    <Button label="Deal" icon="pi pi-users" className="p-button-success" style={{ fontSize: '0.65rem', padding: '0.25rem 0.4rem' }} onClick={() => navigate(`/app/collaborations/${linkedInterest.collaboration.id}`)} />
                                )}
                                <Button icon="pi pi-briefcase" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Offer" tooltipOptions={{ position: 'top' }} onClick={() => navigate(`/app/dsa-offers/${offer?.id || invite.dsa_offer_id}`)} />
                                <Button icon="pi pi-external-link" className="p-button-outlined" style={{ padding: '0.25rem 0.4rem', fontSize: '0.65rem' }} tooltip="View Lead" tooltipOptions={{ position: 'top' }} onClick={() => handleViewLead(invite)} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Turn indicator footer */}
                {isCountered && isYourTurn && (
                    <div className="bg-orange-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-orange-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-sync" style={{ fontSize: '0.55rem' }} />
                            <span>Your turn to respond</span>
                        </div>
                    </div>
                )}
                {isCountered && !isYourTurn && (
                    <div className="bg-yellow-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-yellow-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-clock" style={{ fontSize: '0.55rem' }} />
                            <span>Awaiting response</span>
                        </div>
                    </div>
                )}
                {isNegotiationAccepted && hasCollaboration && (
                    <div className="bg-green-50 px-2 py-1 border-top-1 surface-border">
                        <div className="flex align-items-center gap-1 text-green-700" style={{ fontSize: '0.6rem' }}>
                            <i className="pi pi-check-circle" style={{ fontSize: '0.55rem' }} />
                            <span>Lead Deal Active · Final {formatCommission(effectivePercentage)}</span>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Loading skeleton - viewMode aware
    const renderSkeleton = () => viewMode === 'list' ? (
        <div className="flex flex-column">
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="surface-card border-round shadow-1 mb-2 p-2">
                    <div className="flex align-items-center gap-2">
                        <Skeleton shape="circle" size="1.75rem" />
                        <div className="flex-1">
                            <Skeleton width="60%" height="0.8rem" className="mb-1" />
                            <Skeleton width="80%" height="0.65rem" />
                        </div>
                        <Skeleton width="60px" height="1.75rem" />
                        <Skeleton width="60px" height="1.75rem" />
                    </div>
                </div>
            ))}
        </div>
    ) : (
        <div className="grid">
            {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="col-12 md:col-6 lg:col-4 p-2">
                    <div className="surface-card border-round shadow-1 p-3">
                        <div className="flex align-items-center gap-2 mb-2">
                            <Skeleton shape="circle" size="1.75rem" />
                            <div>
                                <Skeleton width="80px" height="0.8rem" className="mb-1" />
                                <Skeleton width="60px" height="1rem" />
                            </div>
                        </div>
                        <div className="flex gap-2 mb-2">
                            <Skeleton width="60px" height="1.25rem" />
                            <Skeleton width="70px" height="1.25rem" />
                        </div>
                        <Skeleton width="100%" height="0.8rem" className="mb-1" />
                        <Skeleton width="60%" height="0.75rem" className="mb-1" />
                        <Skeleton width="80%" height="0.75rem" className="mb-2" />
                        <div className="flex gap-2">
                            <Skeleton width="60px" height="1.75rem" />
                            <Skeleton width="28px" height="1.75rem" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    // Empty state - compact
    const renderEmpty = (type) => {
        const messages = {
            pending: {
                title: 'No Pending Enquiries',
                detail: 'No enquiries awaiting your response. Keep your payout offers active!'
            },
            accepted: {
                title: 'No Accepted Enquiries',
                detail: 'You haven\'t accepted any enquiries yet. Accept enquiries to start negotiations.'
            },
            all: {
                title: 'No Enquiries',
                detail: 'You haven\'t received any enquiries yet. Keep your payout offers active!'
            }
        };

        const msg = messages[type] || messages.all;

        return (
            <div className="surface-card border-round shadow-1 p-4 text-center">
                <i className="pi pi-inbox text-3xl text-300 mb-2" />
                <h3 className="text-900 font-medium mb-1" style={{ fontSize: '0.95rem' }}>{msg.title}</h3>
                <p className="text-600 mb-3" style={{ fontSize: '0.8rem' }}>{msg.detail}</p>
                <Button
                    label="Manage My Offers"
                    icon="pi pi-list"
                    className="p-button-outlined p-button-sm"
                    onClick={() => navigate('/app/dsa-offers/my-offers')}
                />
            </div>
        );
    };

    // Render pending invites
    const renderPendingInvites = () => {
        if (pendingLoading) return renderSkeleton();
        if (pendingInvites.length === 0) return renderEmpty('pending');
        return (
            <>
                {viewMode === 'list' ? (
                    <div className="flex flex-column">
                        {pendingInvites.map(invite => (
                            <PendingInviteRow key={invite.id} invite={invite} />
                        ))}
                    </div>
                ) : (
                    <div className="grid">
                        {pendingInvites.map(invite => (
                            <div key={invite.id} className="col-12 md:col-6 lg:col-4 p-2">
                                <PendingInviteCard invite={invite} />
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    };

    // Render accepted invites with negotiation flow
    const renderAcceptedInvites = () => {
        if (acceptedLoading) return renderSkeleton();
        if (!acceptedLoaded) {
            return (
                <div className="surface-card border-round shadow-1 p-6 text-center">
                    <i className="pi pi-spinner pi-spin text-4xl text-primary mb-3" />
                    <p className="text-600">Loading accepted invites...</p>
                </div>
            );
        }
        if (acceptedInvites.length === 0) return renderEmpty('accepted');
        return (
            <>
                {viewMode === 'list' ? (
                    <div className="flex flex-column">
                        {acceptedInvites.map(invite => (
                            <AcceptedInviteRow key={invite.id} invite={invite} />
                        ))}
                    </div>
                ) : (
                    <div className="grid">
                        {acceptedInvites.map(invite => (
                            <div key={invite.id} className="col-12 md:col-6 lg:col-4 p-2">
                                <AcceptedInviteCard invite={invite} />
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="received-invites">
            <NavigationTabMenu tabs={MY_PAYOUTS_TABS} />
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Page Header - Compact */}
            <div className="surface-card border-round shadow-1 p-2 mb-2">
                <div className="flex align-items-center justify-content-between">
                    <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>Payout Enquiries</h2>
                    <div className="flex align-items-center gap-1">
                        <Button
                            icon="pi pi-filter"
                            className={`p-button-text p-button-sm ${hasActiveFilters ? 'p-button-warning' : ''}`}
                            style={{ fontSize: '0.7rem', width: '1.75rem', height: '1.75rem' }}
                            onClick={() => setShowFilters(!showFilters)}
                            tooltip="Filters"
                            badge={hasActiveFilters ? '!' : null}
                        />
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-sm"
                            style={{ fontSize: '0.7rem', width: '1.75rem', height: '1.75rem' }}
                            onClick={handleRefresh}
                            loading={pendingLoading || acceptedLoading}
                            tooltip="Refresh"
                        />
                    </div>
                </div>
            </div>

            {/* Filter Panel - Compact flex layout */}
            {showFilters && (
                <div className="surface-card border-round shadow-1 p-2 mb-2">
                    <div className="flex align-items-end gap-2 flex-wrap">
                        {/* Search */}
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '180px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Search</label>
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
                                <InputText
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Lead name, bank..."
                                    className="w-full p-inputtext-sm"
                                    style={{ fontSize: '0.7rem', paddingLeft: '2rem' }}
                                />
                            </span>
                        </div>

                        {/* Loan Type */}
                        <div className="flex-1" style={{ minWidth: '100px', maxWidth: '130px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Loan Type</label>
                            <Dropdown
                                value={loanTypeFilter}
                                options={LOAN_TYPE_OPTIONS}
                                onChange={(e) => setLoanTypeFilter(e.value)}
                                placeholder="All Types"
                                optionLabel="label"
                                optionValue="value"
                                showClear
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>

                        {/* Start Date */}
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '160px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>From Date</label>
                            <Calendar
                                value={startDate}
                                onChange={(e) => setStartDate(e.value)}
                                placeholder="From"
                                className="w-full p-inputtext-sm"
                                showIcon
                                inputStyle={{ fontSize: '0.7rem' }}
                                dateFormat="dd M yy"
                                showButtonBar
                                maxDate={endDate || undefined}
                                pt={{
                                    input: { style: { fontSize: '0.7rem' } },
                                    dropdownButton: { style: { width: '2rem', padding: '0' } },
                                    icon: { style: { fontSize: '0.75rem' } }
                                }}
                            />
                        </div>

                        {/* End Date */}
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '160px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>To Date</label>
                            <Calendar
                                value={endDate}
                                onChange={(e) => setEndDate(e.value)}
                                placeholder="To"
                                className="w-full p-inputtext-sm"
                                showIcon
                                inputStyle={{ fontSize: '0.7rem' }}
                                dateFormat="dd M yy"
                                showButtonBar
                                minDate={startDate || undefined}
                                pt={{
                                    input: { style: { fontSize: '0.7rem' } },
                                    dropdownButton: { style: { width: '2rem', padding: '0' } },
                                    icon: { style: { fontSize: '0.75rem' } }
                                }}
                            />
                        </div>

                        {/* View Toggle + Refresh - Right aligned, Desktop Only */}
                        <div className="hidden md:flex align-items-center gap-1 ml-auto flex-shrink-0">
                            <SelectButton
                                value={viewMode}
                                options={viewOptions}
                                onChange={(e) => e.value && setViewMode(e.value)}
                                optionLabel="icon"
                                itemTemplate={(option) => <i className={option.icon} style={{ fontSize: '0.7rem' }} />}
                                style={{ transform: 'scale(0.85)' }}
                            />
                            <Button
                                icon="pi pi-refresh"
                                className="p-button-text p-button-rounded"
                                onClick={handleRefresh}
                                loading={pendingLoading || acceptedLoading}
                                tooltip="Refresh"
                                tooltipOptions={{ position: 'top' }}
                                style={{ width: '1.75rem', height: '1.75rem' }}
                            />
                        </div>
                    </div>

                    {/* Active filters & Results count */}
                    <div className="flex align-items-center justify-content-between mt-2 pt-2 border-top-1 surface-border">
                        <span className="text-500" style={{ fontSize: '0.7rem' }}>
                            {pendingLoading ? 'Loading...' : `${activeTab === 0 ? pendingPagination.total : acceptedPagination.total} result${(activeTab === 0 ? pendingPagination.total : acceptedPagination.total) !== 1 ? 's' : ''}`}
                            {hasActiveFilters && ` \u2022 filters active`}
                        </span>
                        {hasActiveFilters && (
                            <Button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                className="p-button-text p-button-sm"
                                onClick={clearFilters}
                                style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Summary Pills - Inline compact */}
            <div className="flex flex-wrap gap-2 mb-2">
                <div className="surface-card border-round shadow-1 px-3 py-2 flex align-items-center gap-2">
                    <span className="font-bold text-warning" style={{ fontSize: '0.9rem' }}>{summary.pending || 0}</span>
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>Pending</span>
                </div>
                <div className="surface-card border-round shadow-1 px-3 py-2 flex align-items-center gap-2">
                    <span className="font-bold text-green-600" style={{ fontSize: '0.9rem' }}>{summary.interested || 0}</span>
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>Accepted</span>
                </div>
                <div className="surface-card border-round shadow-1 px-3 py-2 flex align-items-center gap-2">
                    <span className="font-bold text-red-500" style={{ fontSize: '0.9rem' }}>{summary.declined || 0}</span>
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>Declined</span>
                </div>
                <div className="surface-card border-round shadow-1 px-3 py-2 flex align-items-center gap-2">
                    <span className="font-bold text-primary" style={{ fontSize: '0.9rem' }}>{summary.total || 0}</span>
                    <span className="text-500" style={{ fontSize: '0.7rem' }}>Total</span>
                </div>
            </div>

            {/* Tabs with Pagination - Compact */}
            <TabView
                activeIndex={activeTab}
                onTabChange={handleTabChange}
                pt={{
                    navContainer: { style: { fontSize: '0.8rem' } },
                    inkbar: { style: { height: '2px' } }
                }}
            >
                {/* Pending Tab with Pagination */}
                <TabPanel
                    header={
                        <span className="flex align-items-center gap-2">
                            Pending
                            {summary.pending > 0 && <Badge value={summary.pending} severity="warning" style={{ fontSize: '0.6rem', minWidth: '1.1rem', height: '1.1rem', lineHeight: '1.1rem' }} />}
                        </span>
                    }
                    pt={{ headerAction: { style: { padding: '0.5rem 0.85rem', fontSize: '0.8rem' } } }}
                >
                    {renderPendingInvites()}
                    {/* Pagination for Pending Tab - Always rendered to prevent CLS */}
                    <div className="mt-2" style={{ visibility: pendingPagination.total > pendingPagination.rows ? 'visible' : 'hidden' }}>
                        <Paginator
                            first={pendingPagination.first}
                            rows={pendingPagination.rows}
                            totalRecords={pendingPagination.total || 0}
                            onPageChange={handlePendingPageChange}
                            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                            className="justify-content-center"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                            pt={{
                                root: { style: { padding: '0.25rem' } },
                                pageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                firstPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                lastPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                previousPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                nextPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } }
                            }}
                        />
                    </div>
                </TabPanel>

                {/* Accepted Tab with Pagination */}
                <TabPanel
                    header={`Accepted (${summary.interested || 0})`}
                    pt={{ headerAction: { style: { padding: '0.5rem 0.85rem', fontSize: '0.8rem' } } }}
                >
                    {renderAcceptedInvites()}
                    {/* Pagination for Accepted Tab - Always rendered to prevent CLS */}
                    <div className="mt-2" style={{ visibility: acceptedPagination.total > acceptedPagination.rows ? 'visible' : 'hidden' }}>
                        <Paginator
                            first={acceptedPagination.first}
                            rows={acceptedPagination.rows}
                            totalRecords={acceptedPagination.total || 0}
                            onPageChange={handleAcceptedPageChange}
                            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                            className="justify-content-center"
                            style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                            pt={{
                                root: { style: { padding: '0.25rem' } },
                                pageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                firstPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                lastPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                previousPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } },
                                nextPageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } }
                            }}
                        />
                    </div>
                </TabPanel>
            </TabView>

            {/* Accept/Decline Response Dialog */}
            <Dialog
                visible={showResponseDialog}
                onHide={() => !responding && setShowResponseDialog(false)}
                header={
                    <div className="flex align-items-center gap-3">
                        <div
                            className="flex align-items-center justify-content-center border-circle"
                            style={{
                                width: '2.5rem',
                                height: '2.5rem',
                                backgroundColor: responseType === 'accept' ? '#D1FAE5' : '#FEE2E2'
                            }}
                        >
                            <i
                                className={`pi ${responseType === 'accept' ? 'pi-check' : 'pi-times'}`}
                                style={{ color: responseType === 'accept' ? '#059669' : '#DC2626' }}
                            />
                        </div>
                        <div>
                            <div className="text-lg font-semibold text-900">
                                {responseType === 'accept' ? 'Accept Enquiry' : 'Decline Enquiry'}
                            </div>
                            <div className="text-sm text-500">
                                {responseType === 'accept' ? 'Express interest in this lead' : 'Decline this invitation'}
                            </div>
                        </div>
                    </div>
                }
                style={{ width: '480px' }}
                modal
                className="p-fluid"
                closable={!responding}
                closeOnEscape={!responding}
            >
                {selectedInvite && (
                    <div className="flex flex-column gap-4">
                        {/* Lead Owner Card */}
                        <div className="surface-card border-round-lg shadow-1 overflow-hidden">
                            <div
                                className="p-3"
                                style={{
                                    background: 'linear-gradient(135deg, var(--primary-100) 0%, var(--primary-50) 100%)'
                                }}
                            >
                                <div className="flex align-items-center gap-3">
                                    <Avatar
                                        image={selectedInvite.invited_by?.avatar}
                                        icon={!selectedInvite.invited_by?.avatar ? 'pi pi-user' : null}
                                        size="large"
                                        shape="circle"
                                        className="bg-primary text-white shadow-2"
                                        style={{ width: '3.5rem', height: '3.5rem' }}
                                    />
                                    <div className="flex-1">
                                        <div className="text-500 text-xs">INVITED BY</div>
                                        <div className="text-xl font-bold text-900">
                                            {selectedInvite.invited_by?.name || 'Lead Owner'}
                                        </div>
                                    </div>
                                    {selectedInvite.expiry_text && (
                                        <Tag
                                            value={selectedInvite.expiry_text}
                                            style={outlineTagStyle('warning')}
                                            icon="pi pi-clock"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Lead Details */}
                        <div className="surface-50 border-round-lg p-3">
                            <div className="text-500 text-xs font-semibold mb-2">LEAD DETAILS</div>
                            <div className="flex align-items-center justify-content-between">
                                <div>
                                    <div className="font-semibold text-900 text-lg">
                                        {selectedInvite.lead?.borrower_name || 'Lead'}
                                    </div>
                                    <div className="flex align-items-center gap-2 mt-1">
                                        <Tag
                                            value={getDsaLoanTypeConfig(selectedInvite.lead?.product_type).label}
                                            style={outlineTagStyle('info')}
                                            className="text-xs"
                                        />
                                        {selectedInvite.lead?.city && (
                                            <span className="text-500 text-sm">
                                                <i className="pi pi-map-marker mr-1" style={{ fontSize: '0.7rem' }} />
                                                {selectedInvite.lead.city}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-500 text-xs">LOAN AMOUNT</div>
                                    <div className="text-primary font-bold text-xl">
                                        {formatLoanAmount(selectedInvite.lead?.loan_amount)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Commission Display */}
                        <div className="flex gap-3">
                            <div
                                className="flex-1 text-center p-4 border-round-lg"
                                style={{ backgroundColor: 'var(--green-50)', border: '1px solid var(--green-200)' }}
                            >
                                <div className="text-green-700 text-xs font-semibold mb-2">YOU OFFERED TO PAY</div>
                                <div className="text-green-600 font-bold" style={{ fontSize: '2.5rem', lineHeight: 1 }}>
                                    {formatCommission(selectedInvite.offer?.commission_percentage)}
                                </div>
                                <div className="text-green-600 text-xs mt-1">to the Lead Owner on disbursement</div>
                            </div>
                            <div
                                className="flex-1 text-center p-4 border-round-lg"
                                style={{ backgroundColor: 'var(--surface-100)' }}
                            >
                                <div className="text-500 text-xs font-semibold mb-2">BANK</div>
                                <div className="text-900 font-bold text-lg">
                                    {selectedInvite.offer?.bank_name || 'N/A'}
                                </div>
                                <div className="text-500 text-xs mt-1">
                                    {getDsaLoanTypeConfig(selectedInvite.offer?.loan_type).label}
                                </div>
                            </div>
                        </div>

                        {/* Lead Owner's Message */}
                        {selectedInvite.invite_message && (
                            <div className="surface-50 border-round-lg p-3 border-left-3 border-blue-300">
                                <div className="flex align-items-start gap-2">
                                    <i className="pi pi-comment text-blue-500 mt-1" />
                                    <div>
                                        <div className="text-500 text-xs font-semibold mb-1">MESSAGE FROM LEAD OWNER</div>
                                        <div className="text-700 line-height-3">"{selectedInvite.invite_message}"</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Info Message */}
                        {responseType === 'accept' ? (
                            <Message
                                severity="info"
                                className="w-full"
                                content={
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-info-circle" />
                                        <span>By accepting, you'll express interest. The Lead Owner will review and can start a lead deal.</span>
                                    </div>
                                }
                            />
                        ) : (
                            <Message
                                severity="warn"
                                className="w-full"
                                content={
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-exclamation-triangle" />
                                        <span>The Lead Owner will be notified that you've declined this enquiry.</span>
                                    </div>
                                }
                            />
                        )}

                        {/* Response Message Input */}
                        <div className="field">
                            <label className="block text-900 font-semibold mb-2">
                                {responseType === 'accept' ? 'Your Message' : 'Reason for Declining'}
                                <span className="text-400 font-normal ml-1">(Optional)</span>
                            </label>
                            <InputTextarea
                                value={responseMessage}
                                onChange={(e) => setResponseMessage(e.target.value)}
                                rows={3}
                                className="w-full"
                                placeholder={responseType === 'accept'
                                    ? 'Add a message for the Lead Owner...'
                                    : 'Help them understand why you\'re declining...'
                                }
                                maxLength={500}
                            />
                            <div className="text-right text-xs text-400 mt-1">
                                {responseMessage.length}/500
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-content-end gap-2 pt-2">
                            <Button
                                label="Cancel"
                                className="p-button-text"
                                onClick={() => setShowResponseDialog(false)}
                                disabled={responding}
                            />
                            <Button
                                label={responseType === 'accept' ? 'Accept & Express Interest' : 'Decline Enquiry'}
                                icon={responseType === 'accept' ? 'pi pi-check' : 'pi pi-times'}
                                className={responseType === 'accept' ? 'p-button-success p-button-lg' : 'p-button-danger p-button-outlined'}
                                onClick={handleSubmitResponse}
                                loading={responding}
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Respond to Counter Dialog */}
            <RespondToCounterDialog
                visible={showRespondToCounterDialog}
                onHide={() => {
                    setShowRespondToCounterDialog(false);
                    setSelectedInterest(null);
                }}
                interest={selectedInterest}
                onSuccess={() => {
                    // Close dialog, show success toast, and refresh data
                    setShowRespondToCounterDialog(false);
                    setSelectedInterest(null);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Action Completed',
                        detail: 'Your response has been submitted successfully'
                    });
                    handleRefresh();
                }}
            />

            {/* Compact Calendar overrides - font-size and icon sizing */}
            <style>{`
                .received-invites .p-calendar.p-inputtext-sm .p-inputtext {
                    font-size: 0.7rem !important;
                }
                .received-invites .p-calendar.p-inputtext-sm .p-datepicker-trigger {
                    width: 2rem !important;
                    padding: 0 !important;
                }
                .received-invites .p-calendar.p-inputtext-sm .p-datepicker-trigger .p-button-icon,
                .received-invites .p-calendar.p-inputtext-sm .p-datepicker-trigger .p-icon {
                    font-size: 0.75rem !important;
                    width: 0.75rem !important;
                    height: 0.75rem !important;
                }
            `}</style>
        </div>
    );
};

export default ReceivedInvites;
